// client/src/engines/headcount.js
//
// ★ THE ONE FTE-HEADCOUNT DERIVATION.
//
// Before this module, "how many people work here" was answered FOUR times, by four
// private tallies that had drifted apart in DEFINITION while happening to agree in
// VALUE on today's data (diffs/hc-unify-phase0.txt — 432 per-dept cells + 36
// company-total months, 0 mismatches on cid=3, but every divergence mechanism
// unexercised: 0 terminated employees, 0 unmapped departments, 0 'Other'-category
// departments). The four were:
//   A. computePayroll dd[dept][m].hc -> runFullModel -> payrollHC   (per-seat billing)
//   B. ScenariosPage    ed[emp][m].sal > 0                          (scorecard/chart/RPE)
//   C. KPIDashboardPage employees[].monthlyData[m] > 0              (HC card)
//   D. PayrollPage      its own modern.dd rebuild                   (payroll page)
//
// ★ ONE SOURCE OF TRUTH: the engine's per-employee monthly roster `ed`
// (computePayroll's per-person, per-month { sal, commission, on, ... }). Every
// headcount surface now reads from it, through THIS function, and none keeps a
// private tally.
//
// ★ TWO VIEWS, DECLARED — because they are two genuinely different questions, and
// the old code answered them without ever saying which it meant:
//     ACTIVE      sal > 0                        "how many PEOPLE work here"
//     PAYROLL-RUN sal > 0 OR commission > 0      "how many SEATS the payroll run touches"
// They differ on exactly one shape: an employee terminated mid-window whose final
// commission cycle pays out after their last salary month. ACTIVE drops that month;
// PAYROLL-RUN keeps it. That is the documented legacy semantic of the engine's
// aggregation gate (computePayroll's `if (!on && commission === 0) return;`), and it
// is the right answer for per-seat vendor billing and for the Payroll page — whose
// dollar columns include that commission and would otherwise show money with no head.
//
// ★ is_category IS NOT PART OF HEAD-COUNTING. Heads are counted BY DEPARTMENT only.
// The old D path bucketed people through IS_CATEGORY_DISPLAY_TO_SLUG and therefore
// dropped an entire department whose is_category was 'Other' (the slug is null ->
// `if (!slug) continue`). is_category still groups payroll DOLLARS everywhere it did;
// it no longer touches the head tally. Callers that display heads per is_category do
// so by SUMMING departments — a display grouping over a complete per-department
// tally, never a filter on it.
//
// ★ 'unmapped' IS SURFACED, NEVER DROPPED. An employee whose department is absent
// from department_mappings lands in the explicit UNMAPPED_KEY bucket and IS included
// in the totals. Two of the four old surfaces silently dropped such an employee (A
// through a guarded `dd[dept]?.[m]` lookup, D through `if (!ctx) continue`) while a
// third deliberately surfaced it — the same tenant could be counted two ways, and the
// silent side is the dangerous one: a per-seat vendor scoped company-wide would
// under-bill by the unmapped heads, invisibly.
//
// PURE. No React, no DB. Runs once inside computePayroll; every consumer reads the
// result off payrollResult.headcount rather than re-deriving it.

import { deptSlug } from '../utils/seatDepartments';

export const UNMAPPED_KEY = 'unmapped';

/* ⛔ LEGACY_DEPT_TO_HC_KEY DELETED HERE (P4-a). Semitek's dept-NAME -> headcount-key map
   for tenants with zero department_mappings. Its ONLY consumer was the
   `useLegacyFallback === true` arm of buildHeadcountDeptResolver below, which required a
   company flag that is false on all four live tenants and has no writer in the product.
   Measured before deleting: two code references, both inside that arm. */

/**
 * buildHeadcountDeptResolver(deptMappings) -> department NAME -> headcount key.
 *
 * ★ Reads department_mappings rows WITHOUT consulting is_category — that is the whole
 * point. A department mapped to 'Other' is still a department full of people.
 * ★ Keys are `deptSlug(department_name)`, the SAME shared derivation that produces
 * payrollHC's keys and the per-seat resolver's option ids (utils/seatDepartments.js),
 * so the three remain identical BY CONSTRUCTION rather than by agreement.
 */
export function buildHeadcountDeptResolver(deptMappings) {
  const rows = deptMappings || [];

  /* ★ P4-a — the `useLegacyFallback === true` arm is DELETED; it returned Semitek's
   * headcount buckets and could not execute (flag false on 4/4 tenants, no writer).
   * ⓘ P4-b removed the `useLegacyFallback` parameter and its call-site argument. */

  // ★ FAIL LOUD: flag false AND no mappings. Emit NO department keys and route every
  // employee to the explicit 'unmapped' bucket — which deriveHeadcount already counts
  // into the totals, so the HEADCOUNT stays correct while the BREAKDOWN is visibly
  // absent. Nothing is invented; the gap is the output.
  if (!rows.length) {
    return {
      isLegacy: false,
      unmappedTenant: true,
      keys: [],
      keyFor: () => UNMAPPED_KEY,
    };
  }

  const byName = {};
  const keys = [];
  rows.forEach(r => {
    const k = deptSlug(r.department_name);
    byName[r.department_name] = k;
    if (!keys.includes(k)) keys.push(k);
  });
  return {
    isLegacy: false,
    keys,
    keyFor: name => byName[name] || UNMAPPED_KEY,
  };
}

/**
 * deriveHeadcount({ ed, employees, months, deptMappings }) -> both views.
 *
 * @param ed            computePayroll's per-employee roster: { [empId]: { [month]: { sal, commission, on } } }
 * @param employees     the roster (needs .id and .department only)
 * @param months        the month window to derive over
 * @param deptMappings  department_mappings rows (empty/absent -> legacy key map)
 *
 * @returns {{
 *   keys: string[],            // department keys, in department_mappings order
 *   hasUnmapped: boolean,      // true if any counted employee-month fell to 'unmapped'
 *   active:     view,          // sal > 0
 *   payrollRun: view,          // sal > 0 OR commission > 0
 * }}  where view = { byKey: {key:{month:n}}, byDeptName: {name:{month:n}}, total: {month:n} }
 *
 * byKey is what department-bucketed surfaces render; byDeptName is what surfaces
 * keyed by the raw department string (the Payroll page's dd) need. Both are emitted
 * from ONE pass so they can never disagree.
 */
export function deriveHeadcount({ ed, employees, months, deptMappings }) {
  const M = months || [];
  const emps = employees || [];
  const EDS = ed || {};
  const resolver = buildHeadcountDeptResolver(deptMappings);

  const mkView = () => {
    const byKey = {};
    [...resolver.keys, UNMAPPED_KEY].forEach(k => {
      byKey[k] = {};
      M.forEach(m => { byKey[k][m] = 0; });
    });
    const total = {};
    M.forEach(m => { total[m] = 0; });
    return { byKey, byDeptName: {}, total };
  };

  const active = mkView();
  const payrollRun = mkView();
  let hasUnmapped = false;

  emps.forEach(emp => {
    const rows = EDS[emp.id];
    if (!rows) return;                       // no engine roster for this employee
    const name = emp.department || '';
    const key = resolver.keyFor(name);

    [active, payrollRun].forEach(v => {
      if (!v.byKey[key]) { v.byKey[key] = {}; M.forEach(m => { v.byKey[key][m] = 0; }); }
      if (!v.byDeptName[name]) { v.byDeptName[name] = {}; M.forEach(m => { v.byDeptName[name][m] = 0; }); }
    });

    M.forEach(m => {
      const slot = rows[m];
      if (!slot) return;
      const sal = slot.sal || 0;
      const comm = slot.commission || 0;

      // ACTIVE — the person drew a salary this month.
      if (sal > 0) {
        active.byKey[key][m] += 1;
        active.byDeptName[name][m] += 1;
        active.total[m] += 1;
        if (key === UNMAPPED_KEY) hasUnmapped = true;
      }
      // PAYROLL-RUN — the payroll run touched this person this month (salary, or a
      // trailing commission payout after their last salary month).
      // Step 3 CORRECTED THIS COMMENT: it read 'commission/severance'. A severance no
      // longer reaches `sal` (it is its own slot field), and it deliberately creates NO
      // head in either view -- neither ACTIVE nor PAYROLL-RUN. Nothing here changed; the
      // sentence had become false and would have misled the next reader about exactly
      // the thing this commit moved.
      if (sal > 0 || comm > 0) {
        payrollRun.byKey[key][m] += 1;
        payrollRun.byDeptName[name][m] += 1;
        payrollRun.total[m] += 1;
        if (key === UNMAPPED_KEY) hasUnmapped = true;
      }
    });
  });

  return { keys: resolver.keys, hasUnmapped, unmappedTenant: resolver.unmappedTenant === true, active, payrollRun };
}
